import React from 'react';
import styled, { css } from 'styled-components';
import {useTranslations} from "use-intl";

import { convertThumbnailToPictureImage } from '@tager/web-modules';
import { Nullable } from '@tager/web-core';

import { normalizePrice } from '@/utils/common';
import { convertLabel } from '@/components/ProductCard/ProductCard.constants';
import { AvailabilityStatus, ProductType } from '@/typings/model';
import Picture from '@/components/Picture';
import { colors } from '@/constants/theme';
import {useSettingItem} from "@/hooks";

type Props = {
  data: Nullable<ProductType>;
  params: {
    name: string;
    data: Record<string, string>[];
  }[];
};

function CatalogCardView({ params, data }: Props) {
  const currency = useSettingItem('CURRENCY');
  const t = useTranslations();

  return (
    <Component>
      <PictureWrapper>
        <StyledPicture {...convertThumbnailToPictureImage(data?.image)} />
      </PictureWrapper>
      <DescriptionWrapper>
        <DescriptionTop>
          <Name>{data?.name ?? ''}</Name>
          <Subtitle>{data?.subtitle ?? ''}</Subtitle>
        </DescriptionTop>
        <PriceWrapper>
          {data?.discountPrice && data.price !== data.discountPrice ? (
            <>
              {data?.price && (
                <Price hasDiscount>{normalizePrice(data.price)} {currency}</Price>
              )}
              <Price isBlack>{normalizePrice(data.discountPrice)} {currency}</Price>
            </>
          ) : data?.price ? (
            <Price isBlack>{normalizePrice(data.price)} {currency}</Price>
          ) : null}
        </PriceWrapper>
        {data?.availability ? (
          <Avalability type={data.availability}>
            {convertLabel(t, data.availability)}
          </Avalability>
        ) : null}
      </DescriptionWrapper>
      {!!params?.length ? (
        <Fields>
          {params.map((field, index) => (
            <FieldSection sizeParams={field.name === 'SIZE'} key={index}>
              {field.data.length > 0 &&
                field.data.map((param, index) => (
                  <FieldItemWrapper
                    sizeParams={field.name === 'SIZE'}
                    key={index}
                  >
                    <FieldName>{param.name}</FieldName>{' '}
                    <FieldValue>{param.value}</FieldValue>
                  </FieldItemWrapper>
                ))}
            </FieldSection>
          ))}
        </Fields>
      ) : null}
    </Component>
  );
}

export default CatalogCardView;

const Component = styled.div``;

const PictureWrapper = styled.div`
  padding: 5px 0;
`;

const StyledPicture = styled(Picture)`
  height: 120px;
  width: 100%;
  picture,
  div,
  img {
    height: 100%;
    width: 100%;
    object-fit: contain;
    object-position: left;
  }
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 5px;
  height: 100px;

  & > div {
    margin-top: 5px;
  }
`;

const DescriptionTop = styled.div``;

const Name = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
`;

const Subtitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: rgb(168, 168, 168);
`;
const PriceWrapper = styled.div`
  max-width: 460px;
  display: flex;
  align-items: center;
  & > div:not(:last-child) {
    margin-right: 15px;
  }
`;

const Price = styled.div<{
  hasDiscount?: boolean;
  hasDiscountPLus?: boolean;
  isBlack?: boolean;
}>`
  ${({ hasDiscount, hasDiscountPLus, isBlack }) =>
    hasDiscount && !hasDiscountPLus
      ? css`
          font-style: normal;
          line-height: 20px;
          text-decoration-line: line-through;
          font-weight: 500;
          font-size: 14px;
          color: ${colors.darkGrey};
        `
      : hasDiscount && hasDiscountPLus
      ? css`
          font-style: normal;
          line-height: 26px;
          font-weight: 600;
          font-size: 18px;
          text-decoration-line: line-through;
          color: ${colors.black};
        `
      : css`
          font-style: normal;
          line-height: 26px;
          font-weight: 600;
          font-size: 22px;
          color: ${!isBlack ? colors.primary : colors.black};
        `}
`;

const Avalability = styled.div<{ type: Nullable<string> }>`
  margin-top: auto;
  font-size: 14px;
  font-weight: 500;
  ${({ type }) =>
    type === AvailabilityStatus.AVAILABLE
      ? css`
          color: ${colors.green};
        `
      : type === AvailabilityStatus.ON_REQUEST
      ? css`
          color: ${colors.red};
        `
      : type === AvailabilityStatus.OUT_OF_ORDER
      ? css`
          color: ${colors.gray300};
        `
      : css`
          color: ${colors.black};
        `}
`;

const Fields = styled.div`

  margin-top: 10px;
  padding: 10px 0;
  border-top: 1px solid ${colors.gray100};
`;
const FieldSection = styled.div<{ sizeParams?: boolean }>`
  ${({ sizeParams }) =>
    sizeParams
      ? css`
          display: flex;
          align-items: center;
          & > div:not(:first-child) {
            margin-left: 16px;
          }
        `
      : null}

  &:not(:first-child) {
    margin-top: 10px;
  }
`;
const FieldItemWrapper = styled.div<{ sizeParams?: boolean }>`
  ${({ sizeParams }) =>
    sizeParams
      ? css`
          display: flex;
          flex-direction: column;
        `
      : null}
`;
const FieldName = styled.span`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.gray400};
`;
const FieldValue = styled.span`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.black};
`;
